import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import { AtrapaRitmoHud } from './AtrapaRitmoHud';
import { AtrapaRitmoArenaStatic, AtrapaRitmoArenaDynamic } from './AtrapaRitmoArena';
import { AtrapaRitmoBackdrop } from './AtrapaRitmoBackdrop';
import { AtrapaRhythmBlocks } from './AtrapaRhythmBlocks';
import { PercusionCirculos } from './PercusionCirculos';
import { AtrapaRitmoResults } from './AtrapaRitmoResults';
import { buildPercusionBucketRects, type PercusionBucketRect } from '../../lib/percusionCorporal';
import { buildLaneLayouts, type LaneLayout } from '../../lib/percusionPerspective';
import type { RhythmBubble, AtrapaRitmoDifficulty } from '../../lib/game/atrapaRitmoEngine';

interface AtrapaRitmoStageResults {
  score: number;
  hits: number;
  misses: number;
  maxStreak: number;
}

interface AtrapaRitmoStageProps {
  bubbles: RhythmBubble[];
  difficulty: AtrapaRitmoDifficulty;
  timeLeftMs: number;
  score: number;
  streak: number;
  feedback: 'hit' | 'miss' | 'combo' | null;
  beatPhase: number;
  bucketFlashUntil?: Record<number, number>;
  onPlayfieldSizeChange?: (width: number, height: number) => void;
  onTapPercusion: (percusionId: string) => void;
  frozen?: boolean;
  results?: AtrapaRitmoStageResults | null;
  onPlayAgain?: () => void;
  onChangeLevel?: () => void;
}

export const AtrapaRitmoStage = memo(function AtrapaRitmoStage({
  bubbles,
  difficulty,
  timeLeftMs,
  score,
  streak,
  feedback,
  beatPhase,
  bucketFlashUntil = {},
  onPlayfieldSizeChange,
  onTapPercusion,
  frozen = false,
  results = null,
  onPlayAgain,
  onChangeLevel,
}: AtrapaRitmoStageProps) {
  const playfieldRef = useRef<HTMLDivElement>(null);
  const [playfieldW, setPlayfieldW] = useState(800);
  const [playfieldH, setPlayfieldH] = useState(420);
  const [flashNow, setFlashNow] = useState(() => performance.now());
  const prevScore = useRef(score);
  const [pulse, setPulse] = useState(0);
  const onSizeRef = useRef(onPlayfieldSizeChange);
  onSizeRef.current = onPlayfieldSizeChange;

  useEffect(() => {
    if (score > prevScore.current) setPulse((p) => p + 1);
    prevScore.current = score;
  }, [score]);

  useEffect(() => {
    const el = playfieldRef.current;
    if (!el) return;

    const measure = () => {
      const rect = el.getBoundingClientRect();
      const w = Math.floor(rect.width);
      const h = Math.floor(rect.height);
      if (w > 0 && h > 0) {
        setPlayfieldW(w);
        setPlayfieldH(h);
        onSizeRef.current?.(w, h);
      }
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    window.addEventListener('resize', measure);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, []);

  const bucketRects: PercusionBucketRect[] = useMemo(
    () => buildPercusionBucketRects(playfieldW, playfieldH),
    [playfieldW, playfieldH]
  );

  const horizonY = bucketRects[0]?.horizonY ?? playfieldH * 0.78;

  const laneLayouts: LaneLayout[] = useMemo(
    () => buildLaneLayouts(bucketRects, { width: playfieldW, horizonY }),
    [bucketRects, playfieldW, horizonY]
  );

  const hasActiveFlash = useMemo(
    () => Object.values(bucketFlashUntil).some((u) => u > flashNow),
    [bucketFlashUntil, flashNow]
  );

  useEffect(() => {
    if (!hasActiveFlash) return;
    let raf: number;
    const tick = () => {
      setFlashNow(performance.now());
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [hasActiveFlash]);

  return (
    <div
      className={`ar-stage relative flex flex-col flex-1 min-h-0 w-full overflow-hidden ${
        frozen ? 'ar-stage--frozen' : ''
      }`}
    >
      <div className="relative z-20 shrink-0 px-1 sm:px-2 pt-1">
        <AtrapaRitmoHud
          timeLeftMs={timeLeftMs}
          durationMs={difficulty.durationMs}
          score={score}
          streak={streak}
          feedback={feedback}
        />
      </div>

      <div ref={playfieldRef} className="relative flex-1 min-h-[200px] w-full mx-auto overflow-hidden">
        <AtrapaRitmoBackdrop beatPhase={beatPhase} pulse={pulse} />

        <AtrapaRitmoArenaStatic
          width={playfieldW}
          playfieldHeight={playfieldH}
          horizonY={horizonY}
          laneLayouts={laneLayouts}
          bucketFlashUntil={bucketFlashUntil}
          flashNow={flashNow}
        />

        <AtrapaRitmoArenaDynamic
          width={playfieldW}
          playfieldHeight={playfieldH}
          horizonY={horizonY}
          laneLayouts={laneLayouts}
          bubbles={bubbles}
          feedback={feedback}
        />

        <div className="absolute inset-0 z-10 pointer-events-none">
          <AtrapaRhythmBlocks
            bubbles={bubbles}
            laneLayouts={laneLayouts}
            horizonY={horizonY}
          />
        </div>

        <div className="absolute inset-0 z-20 pointer-events-none">
          <div className="relative w-full h-full pointer-events-auto">
            <PercusionCirculos
              bucketRects={bucketRects}
              bubbles={bubbles}
              flashUntil={bucketFlashUntil}
              flashNow={flashNow}
              onTap={onTapPercusion}
              disabled={frozen}
            />
          </div>
        </div>
      </div>

      {results && onPlayAgain && onChangeLevel && (
        <div className="absolute inset-0 z-50 flex items-center justify-center p-3 sm:p-6 ar-results-overlay" role="presentation">
          <AtrapaRitmoResults
            score={results.score}
            hits={results.hits}
            misses={results.misses}
            maxStreak={results.maxStreak}
            difficulty={difficulty}
            onPlayAgain={onPlayAgain}
            onChangeLevel={onChangeLevel}
            asModal
          />
        </div>
      )}
    </div>
  );
});

export default AtrapaRitmoStage;
